import { DashboardConfigurationDependencies } from '../../../../dashboard-config/dashboard-config.model';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  LocalRowColumnSingleFilterValueFilterApplicator,
  LocalRowColumnValueInRangeFilterApplicator,
  LocalRowPassesFilterApplicatorResultToColumnApplicator,
} from '../../../../filters/dashboard_filter_applicators';
import {
  DashboardAutomaticYearsRangeFilter,
  DashboardYearSliderFilter,
} from '../../../../filters/dashboard_filter_implementations';
import {
  createIdNameTransformers,
  granularityHeadingPhraseByFirst,
  groupDetailOf,
} from '../../../../dashboard-config/granularities/granularity-profile';
import { INDEX_VALUE_KEY } from '../index-metric.profile';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { composeHeading, createRouteMethodologyFilter, defineDashboard, resolveProduct } from '../../../_shared/dashboard-axes';
import {
  buildIndexDevelopmentDefaultPreFilterTransformers,
  IndexDevelopmentMetricProfile,
} from './index-ranking-development-metric.profile';
import { DashboardIndexRankingDevelopmentDefinition } from './dashboard-index-ranking-development.definition';
import {
  DashboardIndexRankingDevelopmentShell,
  dashboardIndexRankingDevelopmentShellProfiles,
} from './dashboard-index-ranking-development-shell.profile';

export type CreateDashboardIndexRankingDevelopmentDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardIndexRankingDevelopmentShell,
    IndexDevelopmentMetricProfile
  >;

/**
 * Time filters for `app-chart-bar-chart-with-line`:
 * - `year_bar_chart` → chart-row ranking pane
 * - `years_line_chart` → line Entwicklung pane
 */
function buildIndexRankingLineChartTimeFilters(
  deps: DashboardConfigurationDependencies,
  options: {
    maxYearOffset: number
  },
): DashboardFilter<any>[] {
  return [
    new DashboardYearSliderFilter(deps.store, deps.datasetName, {
      name: 'year_bar_chart',
      showInFilterComponent: false,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_year_bar_chart',
        new LocalRowColumnSingleFilterValueFilterApplicator('year'),
      ),
      maxYearOffset: options.maxYearOffset
    }),
    new DashboardAutomaticYearsRangeFilter(deps.store, deps.datasetName, {
      name: 'years_line_chart',
      showInFilterComponent: false,
      defaultYearRange: 5,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_years_line_chart',
        new LocalRowColumnValueInRangeFilterApplicator('year'),
      ),
      defaultMaxYearOffset: options.maxYearOffset
    }),
  ];
}

export function createDashboardIndexRankingDevelopmentDefinition(
  input: CreateDashboardIndexRankingDevelopmentDefinitionInput,
): DashboardIndexRankingDevelopmentDefinition {
  const shellKey = input.shell;
  const { axes, shell, metric } = resolveProduct(input, dashboardIndexRankingDevelopmentShellProfiles);
  const granularityId = axes.granularity.id;
  const valueKey = INDEX_VALUE_KEY;

  return defineDashboard<DashboardIndexRankingDevelopmentDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading([metric.headingMetric(filterValues, shellKey), granularityHeadingPhraseByFirst(granularityId, filterValues)]),
    filters: (deps) => [
      ...shell.buildGranularityFilters(deps, {
        addSumGermany: metric.stateAddSumGermany,
        excludeSumSubMarkets: shellKey === 'wzClasses' && metric.excludeSumSubMarketsOnWz,
      }),
      ...metric.createMetricFilters(shellKey),
      createRouteMethodologyFilter(deps),
      ...buildIndexRankingLineChartTimeFilters(deps, { maxYearOffset: metric.resolveMaxYearOffset(shellKey) }),
    ],
    preFilterTransformers: () => buildIndexDevelopmentDefaultPreFilterTransformers(metric),
    postFilterTransformers: (deps) => [
      ...metric.createChartValueTransformers(valueKey, shellKey),
      ...metric.createRankTransformers(valueKey, shellKey),
      ...createIdNameTransformers(deps, granularityId),
      ...metric.createDisplayTransformers(shellKey),
    ],
    exportTransformers: () =>
      metric.createExportTransformers(valueKey, shellKey),
    view: {
      valueKey,
      valueCountKey: metric.valueCountKey?.(shellKey),
      valueSalesKey: metric.valueSalesKey,
      valueEmployedKey: metric.valueEmployedKey,
      valueEmployedStateKey:
        shellKey === 'subMarkets' || shellKey === 'districtSubMarkets'
          ? metric.valueEmployedStateKey
          : undefined,
      groupDetail: groupDetailOf(granularityId, 'name'),
      valueColWidth: metric.valueColWidth(shellKey),
      height: '535px',
      valueDisplay: metric.valueDisplay(shellKey),
      popup: metric.popup(shellKey),
    },
  });
}
